/**
 * LanguageSelector Component
 *
 * Dual language selector showing transcription language -> summary language flow.
 * Two separate inline dropdowns with subtle labels above each.
 */

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Language } from '@/types/session';
import { ArrowRight, Globe } from 'lucide-react';

interface LanguageSelectorProps {
  languages: Language[];
  transcriptionLanguage: string;
  summaryLanguage: string;
  onTranscriptionChange: (lang: string) => void;
  onSummaryChange: (lang: string) => void;
  disabled?: boolean;
}

export function LanguageSelector({
  languages,
  transcriptionLanguage,
  summaryLanguage,
  onTranscriptionChange,
  onSummaryChange,
  disabled = false,
}: LanguageSelectorProps) {
  return (
    <div className="flex items-center gap-2">
      <Globe className="h-4 w-4 text-muted-foreground" />

      {/* Transcription Language */}
      <div className="flex flex-col">
        <span className="mb-0.5 text-[10px] text-muted-foreground leading-tight">Talgreining</span>
        <Select value={transcriptionLanguage} onValueChange={onTranscriptionChange} disabled={disabled}>
          <SelectTrigger className="h-7 w-[100px] border-none bg-transparent px-1 text-sm shadow-none hover:bg-slate-50 focus:ring-0">
            <SelectValue placeholder="Tungumál" />
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code} className="text-sm">
                {lang.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Arrow */}
      <ArrowRight className="mt-3 h-3 w-3 text-muted-foreground" />

      {/* Summary Language */}
      <div className="flex flex-col">
        <span className="mb-0.5 text-[10px] text-muted-foreground leading-tight">Samantekt</span>
        <Select value={summaryLanguage} onValueChange={onSummaryChange}>
          <SelectTrigger className="h-7 w-[100px] border-none bg-transparent px-1 text-sm shadow-none hover:bg-slate-50 focus:ring-0">
            <SelectValue placeholder="Tungumál" />
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code} className="text-sm">
                {lang.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
